import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { refreshSession } from "@/lib/api/client";
import { keys } from "@/lib/api/keys";

const STREAM_URL = "/api/v1/inbox/stream";
/** Con el stream caído, la bandeja se refresca sola cada tanto, como antes. */
export const FALLBACK_POLL_MS = 30_000;
/** Reintentos con espera creciente: 1 s, 2 s, 4 s… hasta este tope. */
const MAX_BACKOFF_MS = 30_000;
/** Varios avisos seguidos de la misma conversación se juntan en una sola recarga. */
const BATCH_MS = 250;

interface InboxNotice {
  id: number;
  conversation_id: number;
  kind: string;
}

function refreshConversations(client: QueryClient, ids: Iterable<number>) {
  for (const id of ids) {
    void client.invalidateQueries({ queryKey: keys.inbox.turns(id) });
    void client.invalidateQueries({ queryKey: keys.inbox.events(id) });
    void client.invalidateQueries({ queryKey: keys.inbox.conversation(id) });
  }
  void client.invalidateQueries({ queryKey: ["inbox", "conversations"] });
  void client.invalidateQueries({ queryKey: keys.inbox.unread() });
}

/** Lo que refresca el polling de respaldo: las listas y lo que esté abierto. */
function refreshEverythingOpen(client: QueryClient) {
  void client.invalidateQueries({ queryKey: ["inbox", "conversations"] });
  void client.invalidateQueries({ queryKey: keys.inbox.unread() });
  void client.invalidateQueries({ queryKey: ["inbox", "turns"] });
  void client.invalidateQueries({ queryKey: ["inbox", "events"] });
}

/**
 * La bandeja en vivo. El servidor solo avisa qué conversación cambió; acá se
 * invalidan sus consultas y TanStack Query vuelve a pedir lo que esté en
 * pantalla, con la visibilidad de siempre.
 *
 * Si el stream se cae, se reconecta con espera creciente (renovando la sesión:
 * el servidor lo corta cuando vence) y, mientras tanto, la bandeja vuelve al
 * polling de 30 s. Se monta una vez, en el layout.
 */
export function useInboxStream(enabled = true) {
  const client = useQueryClient();

  useEffect(() => {
    if (!enabled) return;
    let source: EventSource | null = null;
    let attempt = 0;
    let lastId: string | null = null;
    let reconnect: ReturnType<typeof setTimeout> | undefined;
    let poll: ReturnType<typeof setInterval> | undefined;
    let flush: ReturnType<typeof setTimeout> | undefined;
    let closed = false;
    const pending = new Set<number>();

    function startPolling() {
      poll ??= setInterval(() => refreshEverythingOpen(client), FALLBACK_POLL_MS);
    }
    function stopPolling() {
      if (poll !== undefined) clearInterval(poll);
      poll = undefined;
    }
    function notice(conversationId: number) {
      pending.add(conversationId);
      flush ??= setTimeout(() => {
        flush = undefined;
        refreshConversations(client, [...pending]);
        pending.clear();
      }, BATCH_MS);
    }

    function connect() {
      if (closed) return;
      if (typeof EventSource === "undefined") {
        startPolling();
        return;
      }
      const url = lastId ? `${STREAM_URL}?after=${encodeURIComponent(lastId)}` : STREAM_URL;
      const current = new EventSource(url, { withCredentials: true });
      source = current;
      current.onopen = () => {
        attempt = 0;
        stopPolling();
      };
      current.addEventListener("inbox", (event: MessageEvent<string>) => {
        if (event.lastEventId) lastId = event.lastEventId;
        try {
          notice((JSON.parse(event.data) as InboxNotice).conversation_id);
        } catch {
          // Un aviso ilegible no rompe nada: el próximo, o el polling, pone todo al día.
        }
      });
      // Se perdieron avisos (pestaña dormida mucho tiempo): se recarga todo lo de la bandeja.
      current.addEventListener(
        "reset",
        () => void client.invalidateQueries({ queryKey: keys.inbox.all }),
      );
      current.onerror = () => {
        current.close();
        if (source === current) source = null;
        if (closed) return;
        startPolling();
        const wait = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempt);
        attempt += 1;
        reconnect = setTimeout(() => {
          // El servidor corta al vencer la sesión: se renueva antes de volver.
          void refreshSession().finally(connect);
        }, wait);
      };
    }

    connect();
    return () => {
      closed = true;
      source?.close();
      stopPolling();
      if (reconnect !== undefined) clearTimeout(reconnect);
      if (flush !== undefined) clearTimeout(flush);
    };
  }, [client, enabled]);
}
